// Compress an image File to a JPEG data URL bounded by maxDim pixels on the
// longest side. Preserves EXIF orientation implicitly through createImageBitmap.
// Returns { dataUrl, sizeKB, dims: {w,h} }.
export async function compressImage(file, { maxDim = 1600, quality = 0.85 } = {}) {
    if (!file || !file.type?.startsWith("image/")) {
        throw new Error("Not an image file");
    }
    // GIFs would lose animation — pass through as-is
    if (file.type === "image/gif") {
        return {
            dataUrl: await fileToDataUrl(file),
            sizeKB: Math.round(file.size / 1024),
            dims: null,
        };
    }

    let bitmap;
    try {
        bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
        // Fallback path for browsers without createImageBitmap options
        const img = await loadImage(await fileToDataUrl(file));
        bitmap = img;
    }

    const srcW = bitmap.width;
    const srcH = bitmap.height;
    const longest = Math.max(srcW, srcH);
    const scale = longest > maxDim ? maxDim / longest : 1;
    const w = Math.round(srcW * scale);
    const h = Math.round(srcH * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    // Fill white so transparent PNGs → JPEG don't come out black
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);

    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    const sizeKB = Math.round((dataUrl.length * 3) / 4 / 1024);
    return { dataUrl, sizeKB, dims: { w, h } };
}

function fileToDataUrl(file) {
    return new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
    });
}

function loadImage(src) {
    return new Promise((res, rej) => {
        const img = new Image();
        img.onload = () => res(img);
        img.onerror = rej;
        img.src = src;
    });
}
